import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Settings,
  LogOut,
  Menu,
  X,
  Layers,
  Rocket,
  Users,
  CreditCard,
} from "lucide-react";
import Cookies from "js-cookie";
import { clearAuthSessionCookies } from "@/lib/authCookies";
import SiteLogo from "@/components/SiteLogo";

const menuItems = [
  { path: "/partner/dashboard", label: "Overview", icon: LayoutDashboard },
  { path: "/partner/tiers", label: "Tiers & Margin", icon: Layers },
  { path: "/partner/deploy", label: "Deploy Client", icon: Rocket },
  { path: "/partner/clients", label: "Clients", icon: Users },
  { path: "/partner/billing", label: "Billing", icon: CreditCard },
  { path: "/partner/settings", label: "Settings", icon: Settings },
];

const PartnerSidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const email = Cookies.get("email") || "";

  const handleLogout = () => {
    clearAuthSessionCookies();
    navigate("/");
  };

  const isActive = (path) => location.pathname === path;

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="fixed top-4 left-4 z-[60] md:hidden flex items-center justify-center w-10 h-10 rounded-lg bg-[#11151F] border border-partner text-partner hover:bg-[#00D2FF]/5 hover:border-[#00D2FF]/30 transition-all"
        data-testid="partner-sidebar-mobile-toggle"
        aria-label="Open menu"
      >
        <Menu className="w-5 h-5" />
      </button>

      {isOpen && (
        <div
          className="fixed inset-y-0 left-64 right-0 bg-black/60 backdrop-blur-sm z-[65] md:hidden"
          onClick={() => setIsOpen(false)}
          data-testid="partner-sidebar-overlay"
        />
      )}

      {isOpen && (
        <button
          onClick={() => setIsOpen(false)}
          className="fixed top-6 left-[210px] z-[70] md:hidden flex items-center justify-center w-8 h-8 rounded-lg text-partner-muted hover:text-partner hover:bg-white/5 transition-all"
          data-testid="partner-sidebar-close-button"
          aria-label="Close menu"
        >
          <X className="w-5 h-5" />
        </button>
      )}

      <aside
        className={`fixed left-0 top-0 h-full w-64 bg-[#11151F] border-r border-partner flex flex-col z-[65] transition-transform duration-300 ease-in-out md:translate-x-0 ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="partner-sidebar"
      >
        <div className="p-6 border-b border-partner">
          <Link to="/" className="flex items-center gap-3">
            <SiteLogo className="h-6 w-auto" />
            <span className="text-xs font-mono tracking-wider text-partner-accent uppercase">
              Partner
            </span>
          </Link>
          <p className="mt-2 text-sm font-semibold text-partner">Partner Console</p>
        </div>

        <nav className="flex-1 p-4 overflow-y-auto">
          <ul className="space-y-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all font-body text-sm ${
                      active
                        ? "bg-[#00D2FF]/10 text-partner-accent border border-[#00D2FF]/30"
                        : "text-partner-muted hover:text-partner hover:bg-[#080A0F]/80"
                    }`}
                    data-testid={`partner-sidebar-link-${item.path.replace(/\//g, "-")}`}
                  >
                    <Icon className={`w-5 h-5 ${active ? "text-partner-accent" : ""}`} />
                    {item.label}
                  </Link>
                </li>
              );
            })}
          </ul>

          <div className="mt-6 pt-4 border-t border-partner">
            <Link
              to="/partner/deploy"
              className="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-lg bg-[#00D2FF]/10 text-partner-accent border border-[#00D2FF]/30 font-body text-sm font-semibold hover:bg-[#00D2FF]/15 transition-all"
              data-testid="partner-sidebar-deploy-cta"
            >
              <Rocket className="w-4 h-4" />
              Deploy Client
            </Link>
          </div>
        </nav>

        <div className="p-4 border-t border-partner space-y-3">
          {email && (
            <p className="px-4 text-xs text-partner-muted font-body truncate" title={email}>
              {email}
            </p>
          )}
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 rounded-lg w-full text-partner-muted hover:text-red-400 hover:bg-red-500/10 transition-all font-body text-sm"
            data-testid="partner-sidebar-logout"
          >
            <LogOut className="w-5 h-5" />
            Log Out
          </button>
        </div>
      </aside>
    </>
  );
};

export default PartnerSidebar;
